import { get, isEmpty, chain, first, last, indexOf } from "lodash"
import { createSelector } from "reselect"
import { getDates } from "modules/portfolio-root/selectors/dates"
import { percentageFormatter, dollarFormatter } from "helpers/formatters/numbers"
import { formatLongDate } from "helpers/formatters/dates"
import { getMostUsedOrDefaultModelId } from "helpers/models"
import { UPLOAD_CONFIG } from "components/upload/constants"
import { getModels } from "resources/models"

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

const getPortfolios = state => get(state, "global.data.portfolios.data", EMPTY_ARRAY)
const getRawScanResult = (state) => get(state, "modules.experimentPositions.scanResult")
const getSelectedModel = (state) => get(state, "modules.experimentPositions.modelId")
const getExperimentDates = (state) => get(state, "modules.experimentPositions.experimentDates")

export const getFiles = (state) => get(state, "modules.experimentPositions.files")
export const getUploadedFiles = (state) => get(state, "modules.experimentPositions.uploadedFiles")
export const getUploadOption = (state) => get(state, "modules.experimentPositions.uploadOption")
export const getUploadDateTime = (state) => get(state, "modules.experimentPositions.uploadDateTime")
export const getAccount = state => get(state, "global.selected.account")
export const getExperiment = (state) => get(state, "global.data.experimentMeta.data")
export const getResourceInfo = (state) => get(state, "global.data.experimentResourceInfo.data.researchTopic")

export const getExperimentId = (_, props) => get(props, "params.experimentId")
export const getTopicId = (_, props) => get(props, "params.topicId", null)

export const getUploadConfig = () => ({ ...UPLOAD_CONFIG })

export const getModelId = createSelector(
  [getSelectedModel, getModels, getPortfolios],
  (selectedModel, models, portfolios) => {
    if (selectedModel) return selectedModel

    return getMostUsedOrDefaultModelId(portfolios, models)
  },
)

export const getOverlappingDates = createSelector(
  [getDates, getExperimentDates, getRawScanResult],
  (dates, experimentDates, scanResult) => {
    const scanDates = get(scanResult, "scan.dates", EMPTY_ARRAY)
    const useableDates = !isEmpty(dates) ? dates : experimentDates
    return chain(scanDates)
      .map(scanDate => indexOf(useableDates, scanDate.date) >= 0 ? scanDate.date : null)
      .filter(date => date)
      .value()
  },
)

export const getScanResult = createSelector(
  [getRawScanResult],
  (scanResult) => {
    if (scanResult) {
      const scanSummary = get(scanResult, "scan.summary", EMPTY_OBJECT)
      const scanDates = get(scanResult, "scan.dates", EMPTY_ARRAY)

      return {
        ...scanResult,
        summary: {
          items: {
            averageEquity: {
              label: "Average Equity",
              value: dollarFormatter(scanSummary.averageEquity || scanSummary.averageGmv),
            },
            modelCoverage: {
              label: "Model Coverage",
              value: percentageFormatter(scanSummary.averageCoverage / 100),
            },
            numberOfDates: {
              label: "Dates found",
              value: scanDates.length,
            },
            averageSecurities: {
              label: "Average Number of Securities",
              value: scanSummary.averageSecurities,
            },
          },
          from: formatLongDate(get(first(scanDates), "date")),
          to: formatLongDate(get(last(scanDates), "date")),
        },
      }
    }

    return null
  },
)
